import * as React from "react";
import { cn, getWeekStartsOn, getDayLabels } from "@/lib/utils";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "./button";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  format,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
} from "date-fns";
import { ja } from "date-fns/locale";

interface CalendarProps {
  className?: string;
  selected?: Date;
  onSelect?: (date: Date) => void;
  modifiers?: { [key: string]: Date[] };
  modifiersClassNames?: { [key: string]: string };
  dayContent?: (day: Date) => React.ReactNode;
}

function Calendar({ className, selected, onSelect, modifiers, modifiersClassNames, dayContent }: CalendarProps) {
  const [currentMonth, setCurrentMonth] = React.useState(selected || new Date());

  const ws = getWeekStartsOn();
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calStart = startOfWeek(monthStart, { weekStartsOn: ws });
  const calEnd = endOfWeek(monthEnd, { weekStartsOn: ws });
  const days = eachDayOfInterval({ start: calStart, end: calEnd });

  const weekDays = getDayLabels(ws);

  const getModifierClasses = (day: Date) => {
    if (!modifiers || !modifiersClassNames) return '';
    return Object.entries(modifiers)
      .filter(([, dates]) => dates.some(d => isSameDay(d, day)))
      .map(([key]) => modifiersClassNames[key] || '')
      .join(' ');
  };

  return (
    <div className={cn("p-3", className)}>
      <div className="flex items-center justify-between mb-4">
        <Button variant="ghost" size="icon" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <h2 className="text-sm font-medium">
          {format(currentMonth, 'yyyy年 M月', { locale: ja })}
        </h2>
        <Button variant="ghost" size="icon" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-1">
        {weekDays.map(d => (
          <div key={d} className="text-center text-xs text-muted-foreground font-medium py-1">{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {days.map(day => {
          const isCurrentMonth = isSameMonth(day, currentMonth);
          const isSelected = selected && isSameDay(day, selected);
          const isToday = isSameDay(day, new Date());
          const modifierClass = getModifierClasses(day);

          const extra = dayContent?.(day);

          return (
            <button
              key={day.toISOString()}
              onClick={() => onSelect?.(day)}
              className={cn(
                "text-center text-sm rounded-md hover:bg-accent hover:text-accent-foreground transition-colors relative flex flex-col items-center justify-center",
                extra ? "h-12 w-full" : "h-9 w-9",
                !isCurrentMonth && "text-muted-foreground/50",
                isSelected && "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground",
                isToday && !isSelected && "bg-accent text-accent-foreground font-bold",
                modifierClass
              )}
            >
              <span>{format(day, 'd')}</span>
              {extra}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export { Calendar };
